package com.merkost.handpass.admin.pages

import androidx.compose.runtime.*
import com.merkost.handpass.admin.components.*
import com.merkost.handpass.admin.container.LogContainer
import com.merkost.handpass.admin.contract.*
import com.merkost.handpass.api.dto.admin.ModerationLogEntry
import com.merkost.handpass.sharedweb.theme.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import com.merkost.handpass.admin.di.koinContainer
import pro.respawn.flowmvi.compose.dsl.subscribe

@Page("/logs")
@Composable
fun LogsPage() {
    AdminPageScaffold(title = "Moderation Log", currentPath = "/logs", showTitle = false) {
        val container = koinContainer<LogContainer>()
        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is LogAction.Navigate -> navigate(action.path)
                }
            }
            LogContent(state, onIntent = ::intent)
        }
    }
}

@Composable
private fun AdminPageScope.LogContent(state: LogState, onIntent: (LogIntent) -> Unit) {
    AdminPageHeader(
        title = "Moderation log",
        subtitle = "Audit trail of admin actions",
        actions = {
            AdminInput(
                value = state.adminId,
                onChange = { onIntent(LogIntent.SetAdminId(it)) },
                placeholder = "Filter by admin ID (UUID)…",
                widthPx = 280,
            )
            AdminButton(label = "Refresh", onClick = { onIntent(LogIntent.Refresh) }, variant = AdminButtonVariant.Primary, size = AdminButtonSize.SM)
        },
    )

    Div({ style { display(DisplayStyle.Flex); gap(HandpassSpacing.SM); alignItems(AlignItems.Center); property("flex-wrap", "wrap"); property("margin-bottom", "${HandpassSpacing.MD}") } }) {
        Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceMuted.value()) } }) { Text("From:") }
        AdminInput(
            value = state.dateFrom ?: "",
            onChange = { onIntent(LogIntent.SetDateFrom(it.takeIf { v -> v.isNotBlank() })) },
            type = InputType.Date,
        )
        Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceMuted.value()) } }) { Text("To:") }
        AdminInput(
            value = state.dateTo ?: "",
            onChange = { onIntent(LogIntent.SetDateTo(it.takeIf { v -> v.isNotBlank() })) },
            type = InputType.Date,
        )
        if (state.dateFrom != null || state.dateTo != null) {
            AdminButton(
                label = "Clear",
                onClick = {
                    onIntent(LogIntent.SetDateFrom(null))
                    onIntent(LogIntent.SetDateTo(null))
                },
                variant = AdminButtonVariant.Ghost,
                size = AdminButtonSize.SM,
            )
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(HandpassSpacing.SM)
            property("flex-wrap", "wrap"); property("margin-bottom", "${HandpassSpacing.XL}")
        }
    }) {
        LOG_ACTION_FILTERS.forEach { filter ->
            AdminButton(
                label = filter.replace("_", " "),
                onClick = { onIntent(LogIntent.SetActionFilter(filter)) },
                variant = if (state.actionFilter == filter) AdminButtonVariant.Primary else AdminButtonVariant.Secondary,
                size = AdminButtonSize.SM,
            )
        }
    }

    AdminListSection(
        title = "Log entries",
        count = null,
        data = state.data,
        emptyTitle = "No moderation logs",
        skeletonRows = 6,
        onRetry = { onIntent(LogIntent.Refresh) },
    ) { rows ->
        LogTable(rows = rows, state = state, onIntent = onIntent)
    }

    Div({ style { property("margin-top", "${HandpassSpacing.XL}") } }) {
        AdminPagination(
            currentPage = state.page,
            totalPages = state.totalPages,
            onPageChange = { onIntent(LogIntent.GoToPage(it)) },
        )
    }
}

@Composable
private fun LogTable(rows: List<ModerationLogEntry>, state: LogState, onIntent: (LogIntent) -> Unit) {
    AdminTable(
        columns = listOf(
            AdminColumn<ModerationLogEntry>("Timestamp", "160px", sortKey = "created_at") { Text(it.createdAt.take(16).replace("T", " ")) },
            AdminColumn("Admin", "160px") { Text(it.adminName ?: "Unknown") },
            AdminColumn("Action", "180px") { AdminBadge(label = it.action.replace("_", " "), variant = AdminBadgeVariant.Info) },
            AdminColumn<ModerationLogEntry>("Target", "1fr") { log ->
                val targetLabel = log.targetUserName ?: log.targetListingTitle
                if (targetLabel != null) {
                    LogTargetLink(label = targetLabel, onTap = { onIntent(LogIntent.TapTarget(log)) })
                } else {
                    Span({ style { color(OnSurfaceFaint.value()) } }) { Text("—") }
                }
            },
            AdminColumn("Reason", "1fr") { log -> Span({ style { color(OnSurfaceMuted.value()) } }) { Text(log.reason ?: "—") } },
        ),
        rows = rows,
        rowId = { it.id },
        sortBy = state.sortBy,
        sortAsc = state.sortAsc,
        onSort = { key ->
            val newAsc = if (state.sortBy == key) !state.sortAsc else true
            onIntent(LogIntent.SetSort(key, newAsc))
        },
        flush = true,
    )
}

val LogTargetLinkStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.InlineBlock)
            .color(Accent.value())
            .cursor(Cursor.Pointer)
            .borderRadius(RadiusControl.value())
            .textDecorationLine(TextDecorationLine.None)
            .outline(0.px, LineStyle.None, Color("transparent"))
    }
    hover {
        Modifier.textDecorationLine(TextDecorationLine.Underline)
    }
    focusVisible {
        Modifier
            .outline(0.px, LineStyle.None, Color("transparent"))
            .boxShadow(spreadRadius = 3.px, color = AccentRing.value())
    }
}

@Composable
private fun LogTargetLink(label: String, onTap: () -> Unit) {
    A(
        href = "#",
        attrs = LogTargetLinkStyle.toAttrs {
            onClick { event ->
                event.preventDefault()
                onTap()
            }
        },
    ) {
        Text(label)
    }
}
